import { Component, DestroyRef, inject, viewChild } from '@angular/core';
import { KorviuListComponent } from 'shared/components/korviu-list/korviu-list.component';
import { IKorviuListConfig, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { UsersService } from 'core/services/users.service';
import { KorviuDatePickerComponent } from 'shared/components/korviu-date-picker/korviu-date-picker.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { ContactUsModalComponent } from '../../../shared/modal/contact-us-modal/contact-us-modal.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { IFilterSection } from 'shared/types/filter-section.interface';
import { ISortSection } from 'shared/types/sort-section.interface';
import { SortOrders } from 'shared/types/sort-order.enum';
import { IRecentActivityItem, RecentActivityCategory, RecentActivityType } from 'core/types/recent-activity.interface';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { Observable, merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { differenceInCalendarDays, format, isFuture } from 'date-fns';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';
import { Router } from '@angular/router';
import { TaxDocumentationModalComponent } from '../tax-documentation-modal/tax-documentation-modal.component';
import { isAcceptedReferralActivity } from 'shared/utils/recent-activity.utils';

@Component({
  selector: 'app-recent-activity',
  imports: [KorviuListComponent, KorviuDatePickerComponent, TranslatePipe, KorviuButtonComponent, BreadcrumbComponent],
  templateUrl: './recent-activity.component.html',
  styleUrl: './recent-activity.component.scss'
})
export class RecentActivityComponent {
  readonly activityList = viewChild<KorviuListComponent>('activityList');

  readonly #usersService = inject(UsersService);
  readonly #modalService = inject(NzModalService);
  readonly #translateService = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);

  readonly startDateControl = new FormControl<Date | null>(null);
  readonly endDateControl = new FormControl<Date | null>(null);

  readonly sortSections: ISortSection[] = [
    {
      localizableName: 'common.sortOption.date',
      options: [
        {
          localizableName: 'common.sortOption.newest',
          sortDirection: { sortBy: 'date', sortOrder: SortOrders.DESCEND }
        },
        {
          localizableName: 'common.sortOption.oldest',
          sortDirection: { sortBy: 'date', sortOrder: SortOrders.ASCEND }
        }
      ]
    }
  ];

  readonly filterSections: IFilterSection[] = [
    {
      name: '',
      localizableName: 'profile.recentActivityCategory.title',
      parameter_name: 'category',
      checkboxModel: Object.values(RecentActivityCategory).map(category => ({
        label: '',
        localizableName: `profile.recentActivityCategory.${category}`,
        value: category,
        parameter_name: category
      }))
    }
  ];

  readonly listConfig: IKorviuListConfig = {
    getFn: this.#getRecentActivity.bind(this),
    sortSections: this.sortSections,
    filterSections: this.filterSections,
    totalLocalizableName: 'profile.actions',
    itemType: KorviuListItemTypes.RECENT_ACTIVITY,
    detailsFn: this.onActivityClick.bind(this)
  };

  constructor() {
    merge(this.startDateControl.valueChanges, this.endDateControl.valueChanges, this.#translateService.onLangChange)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.activityList()?.reloadData(true));
  }

  readonly disableStartDate = (currentDate: Date): boolean => {
    if (!currentDate) return false;
    if (isFuture(currentDate)) return true;

    const endDate = this.endDateControl.value;
    return endDate ? differenceInCalendarDays(currentDate, endDate) > 0 : false;
  };

  readonly disableEndDate = (currentDate: Date): boolean => {
    if (!currentDate) return false;
    if (isFuture(currentDate)) return true;

    const startDate = this.startDateControl.value;
    return startDate ? differenceInCalendarDays(currentDate, startDate) < 0 : false;
  };

  public showContactUsForm(): void {
    this.#modalService.create({
      nzContent: ContactUsModalComponent,
      nzCentered: true,
      nzClosable: false,
      nzWidth: 554,
      nzWrapClassName: 'modal-wrap'
    });
  }

  public onActivityClick(item: IRecentActivityItem): void {
    if (isAcceptedReferralActivity(item)) {
      return;
    }

    switch (item.activity_type) {
      case RecentActivityType.MISSION_COMPLETED:
        void this.#router.navigate(['/workspace/promotions/missions'], { queryParams: { status: 'completed' } });
        return;
      case RecentActivityType.PROMOTION_ENTERED:
      case RecentActivityType.PROMOTION_WON:
      case RecentActivityType.PROMOTION_CLAIMED:
        this.#navigateToPromotion(item);
        return;
      case RecentActivityType.REFERRAL_SENT:
      case RecentActivityType.REMINDER_SENT:
        this.#navigateToReferral(item);
        return;
      case RecentActivityType.SPROUT_REGISTERED:
      case RecentActivityType.GANARBOL_REACHED:
        this.#navigateToForestMember(item);
        return;
      case RecentActivityType.GANAS_DEPOSITED:
      case RecentActivityType.GANAS_RECEIVED:
      case RecentActivityType.GANAS_SHARED:
      case RecentActivityType.REWARD_REDEEMED:
        this.#navigateToTransaction(item);
        return;
      case RecentActivityType.CARD_ADDED:
        void this.#router.navigate(['/workspace/balance/card-details']);
        return;
      case RecentActivityType.TAX_DOC_ADDED:
        this.#openTaxDocumentation();
    }
  }

  #getRecentActivity(
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting
  ): Observable<IGetPaginatedResponse<IRecentActivityItem[]>> {
    const requestFilters = filters
      .map(filter => (filter.name === 'search' ? { ...filter, value: filter.value.trim() } : filter))
      .filter(filter => filter.name !== 'search' || filter.value.length >= 2);

    if (this.startDateControl.value) {
      requestFilters.push({ name: 'start_date', value: format(this.startDateControl.value, 'yyyy-MM-dd') });
    }

    if (this.endDateControl.value) {
      requestFilters.push({ name: 'end_date', value: format(this.endDateControl.value, 'yyyy-MM-dd') });
    }

    return this.#usersService.getRecentActivity(pagination, requestFilters, sorting);
  }

  #navigateToPromotion(item: IRecentActivityItem): void {
    const campaignId = this.#getMetadataNumber(item, 'campaign_id');
    const commands = campaignId ? ['/workspace/promotions/campaign', campaignId] : ['/workspace/promotions'];

    void this.#router.navigate(commands);
  }

  #navigateToReferral(item: IRecentActivityItem): void {
    const invitationId = this.#getMetadataNumber(item, 'invitation_id');
    const commands = invitationId ? ['/workspace/my-referrals/referral', invitationId] : ['/workspace/my-referrals'];

    void this.#router.navigate(commands);
  }

  #navigateToForestMember(item: IRecentActivityItem): void {
    const commands = item.actor_id ? ['/workspace/my-forest/user', item.actor_id] : ['/workspace/my-forest'];

    void this.#router.navigate(commands);
  }

  #navigateToTransaction(item: IRecentActivityItem): void {
    const queryParams: Record<string, string | number> = { activityDate: item.date };
    const source = this.#parseSourceId(item.source_id);

    if (source?.label === 'balance.balancetransaction') {
      queryParams['transactionId'] = source.id;
    } else if (source?.label === 'products.productpurchase') {
      queryParams['purchaseId'] = source.id;
    } else {
      const earnedMonth = this.#getMetadataString(item, 'earned_month');

      if (earnedMonth) {
        queryParams['earnedMonth'] = earnedMonth;
      }
    }

    void this.#router.navigate(['/workspace/balance/transactions'], { queryParams });
  }

  #openTaxDocumentation(): void {
    this.#modalService.create({
      nzContent: TaxDocumentationModalComponent,
      nzWrapClassName: 'modal-wrap',
      nzClosable: false,
      nzWidth: 555,
      nzClassName: 'modal-dialog'
    });
  }

  #parseSourceId(sourceId: string): { label: string; id: number } | null {
    const [, label, rawId] = sourceId.split(':');
    const id = Number(rawId);

    return label && Number.isInteger(id) ? { label, id } : null;
  }

  #getMetadataNumber(item: IRecentActivityItem, key: string): number | null {
    const value = Number(item.metadata[key]);

    return Number.isInteger(value) && value > 0 ? value : null;
  }

  #getMetadataString(item: IRecentActivityItem, key: string): string | null {
    const value = item.metadata[key];

    return typeof value === 'string' && value ? value : null;
  }
}
